import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { merge, Observable, of, Subject } from 'rxjs';
import { map, switchMap, publishReplay, refCount, startWith } from 'rxjs/operators';
import { CovanceGridColumn } from '../../../covance-ag-grid';
import { zcProjectNumberColumn } from '../../../documents';
import { blindingRoleColumn, buildDateColumn, emailColumn, protocolNameColumn, sponsorNameColumn, studyRoleColumn, userNameColumn, countryColumn, getCompositeSiteColumn, activeSiteColumn } from '../../../shared';
import { SearchFieldsValues } from './admin-protocol-access-filter/admin-protocol-access-filter.component';
import { AdminProtocolAccessesStorage } from './admin-protocol-accesses-storage';
import { AdminProtocolAccessReport } from '../report-types/admin-activity-report-protocol-access';
import { FilterFieldValue, CovanceFilter } from '../../../covance-filter';
import { AdminActivityReportsFilterResolver } from '../documents/admin-activity-reports-filter-resolver';

@Component({
    selector: 'admin-protocol-access-reports',
    template: require('./admin-protocol-access-reports.component.html'),
    styles: [require('./admin-protocol-access-reports.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminProtocolAccessReportComponent {
    public readonly protocolControl: FormControl;
    public readonly columnDefs: CovanceGridColumn[];
    public readonly reports$: Observable<AdminProtocolAccessReport[]>;
    public readonly filterGroup = 'protocol-access-report';
    public readonly filterFields$: Observable<FilterFieldValue[]>;
    private readonly searchFieldValues$ = new Subject<SearchFieldsValues>();

    constructor(
        protocolAccessesStorage: AdminProtocolAccessesStorage,
        filterService: CovanceFilter,
        filterResolver: AdminActivityReportsFilterResolver,
    ) {
        this.protocolControl = new FormControl();
        const filterDefs = filterResolver.resolveEQueryFilters();

        this.columnDefs = this.getColumnDefs();

        const filterFields$ = this.searchFieldValues$.pipe(
            map(values => {
                filterService.setFiltersItems([], filterDefs);
                return { fields: filterDefs, values };
            }),
            publishReplay(1),
            refCount(),
        );

        this.filterFields$ = filterFields$.pipe(map(x => x.fields));

        const filterFlow$ = filterFields$
            .pipe(
                switchMap(({ fields, values }) => filterService.listen(fields).pipe(map(() => ({ fields, values })))),
                publishReplay(1),
                refCount()
            );

        this.reports$ = filterFlow$.pipe(
            switchMap(({ fields, values }) =>
                protocolAccessesStorage.getReports(values)
            ),
            publishReplay(1),
            refCount(),
            startWith([]),
        );
    }

    public onSearch(values: SearchFieldsValues) {
        if (!this.areSearchFieldsEmpty(values)) {
            this.searchFieldValues$.next(values);
        }
    }

    private getColumnDefs(): CovanceGridColumn[] {
        const columns: CovanceGridColumn[] = [
            sponsorNameColumn,
            protocolNameColumn,
            zcProjectNumberColumn,
            getCompositeSiteColumn('asc'),
            countryColumn,
            userNameColumn,
            emailColumn,
            studyRoleColumn,
            blindingRoleColumn,
            buildDateColumn<AdminProtocolAccessReport, 'accountCreatedDate'>('accountCreatedDate', 'Account Created Date', 'desc'),
            buildDateColumn<AdminProtocolAccessReport, 'accessGivenDate'>('accessGivenDate', 'Access Given Date', 'desc'),
            buildDateColumn<AdminProtocolAccessReport, 'accessRevokedDate'>('accessRevokedDate', 'Access Revoked Date'),
            buildDateColumn<AdminProtocolAccessReport, 'lastAccessDate'>('lastAccessDate', 'Last Login Date'),
            activeSiteColumn
        ];

        return columns;
    }

    private areSearchFieldsEmpty(values: SearchFieldsValues) {
        return !values.email
            && !values.protocol;
    }
}
